import type { ProfileOnboardingLocationState } from "../types/profileOnboarding.d";
import type { PersonalDetailsFormValues } from "../validation/personalDetailsSchema";
import type { PlayerDetailsFormValues } from "../validation/playerDetailsSchema";
import type { UmpireDetailsFormValues } from "../validation/umpireDetailsSchema";
import type { ScorerDetailsFormValues } from "../validation/scorerDetailsSchema";
import type { OtherInfoFormValues } from "../validation/otherInfoSchema";
import type {
  PlayerV2UpdateBody,
  PlayerV2Gender,
  PlayerV2Currency,
  PlayerV2Frequency,
  PlayerV2UserStatus,
  PlayerV2ScorerDetails,
} from "../types/playerV2.d";

export interface ProfileOnboardingDraft {
  personal?: PersonalDetailsFormValues & { profileImage?: File | null };
  player?: PlayerDetailsFormValues;
  umpire?: UmpireDetailsFormValues;
  scorer?: ScorerDetailsFormValues;
  other?: OtherInfoFormValues & { emergencyCountryCode?: number };
}

function mapGender(g?: string): PlayerV2Gender {
  if (g === "Female") return "FEMALE";
  if (g === "Other") return "OTHER";
  return "MALE";
}

function stripPhone(digits: string): string {
  return digits.replace(/\D/g, "");
}

function parseFloatSafe(v: string | undefined, fallback = 0): number {
  if (v == null || v === "") return fallback;
  const n = parseFloat(String(v).replace(/,/g, ""));
  return Number.isFinite(n) ? n : fallback;
}

function parseIntSafe(v: string | undefined, fallback = 0): number {
  if (v == null || v === "") return fallback;
  const n = parseInt(String(v).replace(/\D/g, ""), 10);
  return Number.isFinite(n) ? n : fallback;
}

/** RELATION: form uses API enum values; accept legacy lowercase keys. */
function mapRelation(ui: string): string {
  if (/^[A-Z_]+$/.test(ui)) return ui;
  const m: Record<string, string> = {
    spouse: "SPOUSE",
    parent: "OTHER",
    sibling: "BROTHER",
    friend: "FRIEND",
    other: "OTHER",
    father: "FATHER",
    mother: "MOTHER",
    brother: "BROTHER",
    sister: "SISTER",
    son: "SON",
    daughter: "DAUGHTER",
  };
  return m[ui.toLowerCase()] ?? "OTHER";
}

function mapUmpireFrequency(ui: string): PlayerV2Frequency {
  return ui === "MATCH" ? "MATCH" : "HOUR";
}

/** API enums reject empty strings; use when role is off or step not filled yet. */
const DEFAULT_PLAYER_ROLE = "BATSMAN";
const DEFAULT_BATTING_STYLE = "RIGHT_HANDED";
const DEFAULT_BOWLING_STYLE = "RIGHT_ARM_FAST";

function pickPlayerEnum(
  isPlayer: boolean,
  pl: PlayerDetailsFormValues | undefined,
  key: keyof Pick<PlayerDetailsFormValues, "playerRole" | "battingStyle" | "bowlingStyle">,
  fallback: string
): string {
  if (!isPlayer) return fallback;
  const v = pl?.[key]?.trim();
  return v && v.length > 0 ? v : fallback;
}

/** API expects YYYY-MM-DD; normalize date inputs from the browser. */
function normalizeDob(raw: string | undefined): string {
  const t = raw?.trim();
  if (!t) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
  const d = new Date(t);
  if (!Number.isNaN(d.getTime())) {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  }
  return t.slice(0, 10);
}

/** Java Instant/OffsetDateTime: noon UTC on calendar day avoids TZ shifting the date. */
function toIsoDobFromYmd(ymd: string): string {
  const t = ymd?.trim();
  if (!t) return "";
  const core = /^\d{4}-\d{2}-\d{2}$/.test(t) ? t : normalizeDob(t);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(core)) return "";
  return `${core}T12:00:00.000Z`;
}

const EMPTY_SCORER_FORM: ScorerDetailsFormValues = {
  yearsOfExperience: "",
  matchesFormatWorkedOn: "",
  familiarScoringMethod: "",
  pricingPerMatch: "",
  scoringInterfacePreference: "",
  preferredCity: "",
  pricingPerDay: "",
  languageKnown: "",
};

function buildScorerDetails(s: ScorerDetailsFormValues): PlayerV2ScorerDetails {
  return {
    experienceYears: parseIntSafe(s.yearsOfExperience, 0),
    city: s.preferredCity?.trim() ?? "",
    familiarScoringMethods: s.familiarScoringMethod ? [s.familiarScoringMethod] : ["Manual"],
    matchFormatsWorkedOn: s.matchesFormatWorkedOn ? [s.matchesFormatWorkedOn] : ["Limited Overs"],
    scoringInterfacePreferred: s.scoringInterfacePreference
      ? [s.scoringInterfacePreference]
      : ["Mobile"],
    pricePerMatch: parseFloatSafe(s.pricingPerMatch, 0),
    pricePerDay: parseFloatSafe(s.pricingPerDay, 0),
    languagesKnown: s.languageKnown ? [s.languageKnown] : ["English"],
    bio: "",
  };
}

export function buildPlayerV2Payload(
  locationState: ProfileOnboardingLocationState,
  draft: ProfileOnboardingDraft,
  userStatus: PlayerV2UserStatus
): PlayerV2UpdateBody {
  const p = draft.personal;
  const mobile = stripPhone(locationState.mobileNumber ?? "");
  const email = locationState.email ?? "";
  const rawCc = locationState.countryCode ?? 91;
  const countryCode = rawCc === 0 ? 91 : rawCc;

  const isPlayer = p?.isPlayer ?? false;
  const isUmpire = p?.isUmpire ?? false;
  const isScorer = p?.isScorer ?? false;

  const pl = draft.player;
  const um = draft.umpire;
  const sc = draft.scorer;
  const o = draft.other;

  let emergencyNo = "";
  if (o?.emergencyContactNumber) {
    emergencyNo = stripPhone(o.emergencyContactNumber);
  }

  const currency: PlayerV2Currency = "INR";
  let experience = 0;
  let fees = 0;
  let frequency: PlayerV2Frequency = "HOUR";
  let locations: string[] = [];

  if (isUmpire && um) {
    experience = parseFloatSafe(um.experienceYears, 0);
    fees = parseFloatSafe(um.feesPerHour, 0);
    frequency = mapUmpireFrequency(um.paymentFrequency || "HOUR");
    const loc = um.umpireLocationAvailability?.trim();
    locations = loc ? loc.split(/[,;]/).map((s) => s.trim()).filter(Boolean) : [];
  }

  const dobYmd = normalizeDob(p?.dateOfBirth);
  const dobIso = toIsoDobFromYmd(dobYmd);

  const body: PlayerV2UpdateBody = {
    playerId: String(locationState.userId ?? "").trim(),
    gender: mapGender(p?.gender),
    emailId: email,
    phoneNo: mobile,
    countryCode,
    battingStyle: pickPlayerEnum(isPlayer, pl, "battingStyle", DEFAULT_BATTING_STYLE),
    bowlingStyle: pickPlayerEnum(isPlayer, pl, "bowlingStyle", DEFAULT_BOWLING_STYLE),
    emergencyName: o?.emergencyContactName?.trim() || "",
    emergencyNo,
    stAddress: o?.address?.trim() || "",
    zipCode: o?.zipCode?.trim() || "",
    state: o?.state?.trim() || "",
    desc: o?.description?.trim() || "",
    playerFirstName: p?.firstName?.trim() || "",
    playerLastName: p?.lastName?.trim() || "",
    playerMiddleName: p?.middleName?.trim() || "",
    playerRole: pickPlayerEnum(isPlayer, pl, "playerRole", DEFAULT_PLAYER_ROLE),
    DOB: dobYmd,
    dob: dobIso || (dobYmd && /^\d{4}-\d{2}-\d{2}$/.test(dobYmd) ? `${dobYmd}T12:00:00.000Z` : ""),
    userStatus,
    isPartOfLeague: false,
    isUmpire,
    isScorer,
    experience,
    fees,
    currency,
    frequency,
    locations,
    phoneNoVerified: false,
    emailIdVerified: false,
    smsEnabled: true,
    emailEnabled: true,
    emergencyContactRelation: o?.relation ? mapRelation(o.relation) : "OTHER",
  };

  if (isScorer) {
    body.scorerDetails = buildScorerDetails(sc ?? EMPTY_SCORER_FORM);
  }

  return body;
}
